import { requisicaoPublica } from './http';

/**
 * Calculadora pública de crédito de ICMS.
 *
 * ## RN-615 — nenhuma fórmula aqui
 *
 * `totalSped`, `percentSped`, `valueSped`, `percentBeneficio`, `valueBeneficio`
 * e `total` chegam **prontos** do backend, que por sua vez só traduz o que o
 * `@deca/domain` calculou (RN-005 a RN-013, D-002). O front não reimplementa a
 * tabela de percentuais por faixa, não aplica deságio e não recalcula nada — o
 * único número que ele produz é o somatório de exibição, e mesmo esse vem
 * pronto em `totais`.
 *
 * Isso não é preciosismo: no legado, dois geradores de tabela interpretaram o
 * mesmo `percentSped` em escalas diferentes e o contribuinte recebeu por e-mail
 * um percentual 100× maior que o real (RN-078). Uma fórmula duplicada é uma
 * fórmula que vai divergir.
 *
 * Escala dos percentuais: 0–100 (o backend serializa `.percentual`), não fração.
 */

export interface ItemCalculadoraManual {
  /** Competência no formato `AAAA-MM`. */
  readonly competencia: string;
  readonly totalSped: number;
}

export interface EntradaCalculadoraManual {
  readonly itens: readonly ItemCalculadoraManual[];
  readonly regime?: 'INDUSTRIAL' | 'COMERCIAL';
  /**
   * Empresa e CNPJ só existem no lançamento manual: no cálculo por SPED o
   * contribuinte vem do registro 0000 do arquivo. Aqui não há arquivo, e o que
   * a pessoa digita é a única fonte — opcional, porque o cadastro completa.
   */
  readonly empresa?: string;
  readonly cnpj?: string;
}

export interface LinhaCalculo {
  readonly competencia: string;
  readonly regime: string;
  readonly totalSped: number;
  /** Percentual (0–100), não fração — ver RN-078. */
  readonly percentSped: number;
  readonly valueSped: number;
  readonly percentBeneficio: number;
  readonly valueBeneficio: number;
  readonly total: number;
}

export interface ResultadoCalculadora {
  /** Quem é o contribuinte, lido do registro 0000 do SPED. Ausente no manual. */
  readonly contribuinte?: {
    readonly razaoSocial: string;
    readonly cnpj: string;
  };
  readonly linhas: readonly LinhaCalculo[];
  readonly totais: {
    readonly totalSped: number;
    readonly valueSped: number;
    readonly valueBeneficio: number;
    readonly total: number;
  };
  /** RN-075 — meses descartados pelo domínio, com o motivo. */
  readonly ignorados: readonly { readonly competencia: string; readonly motivo: string }[];
  readonly avisos: readonly { readonly codigo: string; readonly mensagem: string }[];
}

/**
 * RN-101 — Quem está simulando. Obrigatório nas duas calculadoras.
 *
 * A calculadora é ferramenta de captação: cada uso precisa produzir um lead com
 * contato utilizável, e por isso a identificação é a **primeira** etapa, não um
 * formulário opcional embaixo do resultado.
 */
export interface Identificacao {
  readonly nome: string;
  readonly emailAddress: string;
  readonly telefone: string;
  /** Vínculo com a empresa calculada — uma das `VINCULOS_COM_A_EMPRESA`. */
  readonly vinculo: VinculoComAEmpresa;
  /** "Gerencio uma carteira de empresas distintas que adquirem Créditos de ICMS". */
  readonly perfilAssessor: boolean;
}

/** As mesmas opções da tela de simulação do Deca 1, na mesma ordem. */
export const VINCULOS_COM_A_EMPRESA = [
  'Sócio',
  'Decisor',
  'Financeiro',
  'Contábil',
  'Outro',
] as const;
export type VinculoComAEmpresa = (typeof VINCULOS_COM_A_EMPRESA)[number];

/**
 * Em que pé está o pré-cadastro que recebeu a simulação.
 *
 * O servidor reaproveita o pré-cadastro mais recente do e-mail — inclusive um
 * já aprovado ou rejeitado. É o que decide o próximo passo da tela: só
 * `RASCUNHO` tem um cadastro a finalizar; `APROVADO` já tem conta e vai para
 * o login; `REJEITADO` fala com o suporte.
 */
export type SituacaoDoCadastro = 'RASCUNHO' | 'APROVADO' | 'REJEITADO';

/** O que a rota devolve: o cálculo, o lead que ele produziu e o suporte. */
export interface SimulacaoRegistrada {
  /** O pré-cadastro que recebeu a simulação — é para onde "Finalizar meu cadastro" leva. */
  readonly registroId: string;
  readonly situacaoDoCadastro: SituacaoDoCadastro;
  readonly simulacoesGravadas: number;
  readonly arquivosGuardados: number;
  readonly ensaio?: boolean;
  readonly resultado: ResultadoCalculadora;
  /** Só dígitos com DDI; vem da configuração do servidor, não do código. */
  readonly suporteWhatsapp: string;
}

/**
 * O link do botão "Contatar suporte": abre o WhatsApp com a mesma mensagem
 * pronta do Deca 1, para quem atende saber de onde a pessoa veio.
 */
export function linkDoSuporte(numero: string): string {
  const mensagem = encodeURIComponent('Olá, fiz o meu calculo e preciso de ajuda');
  return `https://wa.me/${numero.replace(/\D/g, '')}?text=${mensagem}`;
}

/** `POST /api/icms/public/calculadora-manual` — simula e registra o lead. */
export async function calcularManual(
  entrada: EntradaCalculadoraManual,
  quem: Identificacao,
): Promise<SimulacaoRegistrada> {
  return requisicaoPublica<SimulacaoRegistrada>('/icms/public/calculadora-manual', {
    metodo: 'POST',
    corpo: { ...quem, ...entrada },
  });
}

/** RN-102 — teto por arquivo, o mesmo do `calculadoraSpedSchema` no servidor. */
export const TAMANHO_MAXIMO_SPED = 8 * 1024 * 1024;

/** Um ano fiscal completo, que é o que a simulação precisa. */
export const MAXIMO_ARQUIVOS_SPED = 12;

/**
 * Recusa no cliente, antes de subir.
 *
 * A mensagem já vem com o nome do arquivo porque é ela que o `ErroDaApi` exibe:
 * "acima do limite" sem dizer qual dos doze arquivos não ajuda ninguém.
 */
export class ArquivoSpedRecusado extends Error {
  constructor(
    readonly arquivo: string,
    motivo: string,
  ) {
    super(arquivo === '' ? motivo : `${arquivo}: ${motivo}`);
    this.name = 'ArquivoSpedRecusado';
  }
}

/**
 * Lê o arquivo como texto, decidindo a codificação pelo conteúdo.
 *
 * O EFD ICMS/IPI é texto delimitado por `|`, e na prática sai em ISO-8859-1 da
 * maioria dos emissores — mas alguns geram UTF-8. Decodificar Latin-1 como UTF-8
 * não quebra número nem delimitador (são ASCII), então o cálculo sairia certo de
 * qualquer jeito; o que estraga é a **razão social** do registro 0000, que viria
 * com `` no lugar dos acentos.
 *
 * `fatal: true` transforma sequência inválida em exceção, o que dá o teste que
 * interessa: se decodifica como UTF-8, era UTF-8; se não, é Latin-1. A ordem
 * importa — windows-1252 aceita qualquer byte e nunca falharia.
 */
async function lerTexto(arquivo: File): Promise<string> {
  const bytes = await arquivo.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/**
 * `POST /api/icms/public/calculadora-sped` — 5 chamadas por minuto por IP.
 *
 * O conteúdo vai como texto no corpo JSON, e não como `multipart/form-data`,
 * porque é isso que a rota aceita. Os arquivos **ficam guardados** junto da
 * simulação: quem for atender o lead precisa poder conferir a apuração que
 * gerou o número.
 *
 * O parse do SPED **não** acontece no navegador, de propósito: é regra fiscal
 * (registros 0000/0005/E110/E111, RN-001 a RN-004), e regra fiscal mora no
 * domínio, com teste. Uma segunda implementação no front seria a mesma
 * divergência do RN-078 esperando para acontecer.
 *
 * Os limites abaixo repetem os do servidor de propósito. Não é confiança no
 * cliente — o servidor valida de novo, e é ele quem manda; é para o usuário que
 * arrastou 30 arquivos saber disso antes de esperar um upload que terminaria em
 * 400.
 */
export async function calcularPorSped(
  arquivos: readonly File[],
  quem: Identificacao,
): Promise<SimulacaoRegistrada> {
  if (arquivos.length > MAXIMO_ARQUIVOS_SPED) {
    throw new ArquivoSpedRecusado(
      '',
      `Máximo de ${MAXIMO_ARQUIVOS_SPED} arquivos por simulação — envie um ano por vez.`,
    );
  }

  const lidos = await Promise.all(
    arquivos.map(async (arquivo) => {
      const conteudo = await lerTexto(arquivo);

      if (conteudo.length === 0) {
        throw new ArquivoSpedRecusado(arquivo.name, 'Arquivo vazio.');
      }
      if (conteudo.length > TAMANHO_MAXIMO_SPED) {
        throw new ArquivoSpedRecusado(
          arquivo.name,
          `Acima do limite de ${TAMANHO_MAXIMO_SPED / 1024 / 1024} MB.`,
        );
      }

      return { nome: arquivo.name, conteudo };
    }),
  );

  return requisicaoPublica<SimulacaoRegistrada>('/icms/public/calculadora-sped', {
    metodo: 'POST',
    corpo: { ...quem, arquivos: lidos },
  });
}

/** RN-613 — só `.txt`, por arrastar-e-soltar ou por seleção manual. */
export const EXTENSOES_SPED_ACEITAS = '.txt,text/plain';

export function ehArquivoSpedAceito(arquivo: File): boolean {
  // O `type` do navegador é pouco confiável (vem vazio em vários casos), então
  // a extensão manda. RN-640: não há validação de layout no cliente — quem sabe
  // se o conteúdo é um SPED válido é o parser do domínio.
  return arquivo.name.toLowerCase().endsWith('.txt');
}
